/**
 * Personality Relationships
 * -------------------------
 * Defines which secondary personality profiles are valid for each
 * primary personality profile.
 *
 * Every primary profile may be paired with any of the remaining
 * five personality profiles.
 *
 * The Personality Engine will later evaluate these candidates and
 * determine the strongest secondary profile for the student.
 */

export const PERSONALITY_RELATIONSHIPS = {

  analytical_thinker: [

  {
    profile: "creative_explorer",

    introduction:
        "You are primarily an Analytical Thinker, but you also share strong characteristics with the Creative Explorer profile.",

    blendSummary:
        "While logic is your natural starting point, you also enjoy exploring new ideas and considering different possibilities. You balance careful analysis with curiosity, allowing you to approach challenges creatively without losing sight of evidence and practical reasoning.",

    whyThisCombinationWorks:
        "Your analytical thinking gives structure to your ideas, while your creativity encourages you to explore new possibilities. Together, these qualities help you develop solutions that are both innovative and well reasoned.",      

    blendStrengths: [
        "Creative problem-solving",
        "Critical thinking",
        "Innovation",
        "Curiosity"
    ],

    learningStyle:
        "You learn best when you first understand concepts deeply and then experiment with different ways of applying them.",

    idealEnvironment:
        "You thrive in environments that encourage both structured thinking and creative exploration."
    },

  {
    profile: "strategic_planner",

    introduction:
        "You are primarily an Analytical Thinker, but you also share strong characteristics with the Strategic Planner profile.",

    blendSummary:
        "You enjoy understanding complex problems through careful analysis while naturally thinking several steps ahead. Rather than making quick decisions, you prefer gathering information, evaluating alternatives and developing structured plans before taking action.",

    whyThisCombinationWorks:
  "Your analytical mindset helps you understand complex problems, while your strategic thinking enables you to organise ideas into clear, long-term plans. Together, these qualities allow you to make thoughtful decisions with confidence and purpose.",

    blendStrengths: [
        "Analytical reasoning",
        "Strategic planning",
        "Logical decision-making",
        "Organisation"
    ],

    learningStyle:
        "You learn best through structured material that encourages critical thinking and long-term application.",

    idealEnvironment:
        "You thrive in organised environments where careful analysis, planning and thoughtful decision-making are valued."
 },

  {
    profile: "versatile_collaborator",

    introduction:
        "You are primarily an Analytical Thinker, but you also share strong characteristics with the Versatile Collaborator profile.",

    blendSummary:
        "Your analytical nature is balanced by an ability to work effectively with others. You enjoy solving problems logically while remaining open to different perspectives, making you both thoughtful and approachable in team environments.",

    whyThisCombinationWorks:
        "Your logical thinking provides careful analysis, while your collaborative nature encourages you to consider different perspectives. Together, these qualities help you solve problems effectively while working well with others.",

    blendStrengths: [
        "Logical reasoning",
        "Communication",
        "Teamwork",
        "Balanced decision-making"
    ],

    learningStyle:
        "You enjoy learning through thoughtful discussion, collaborative projects and opportunities to analyse ideas with others.",

    idealEnvironment:
        "You thrive in environments that combine intellectual challenge with teamwork and open communication."
  },

  {
    profile: "practical_operator",

    introduction:
        "You are primarily an Analytical Thinker, but you also share strong characteristics with the Practical Operator profile.",

    blendSummary:
        "You enjoy analysing problems thoroughly, but you also value practical solutions that can be applied effectively. Rather than remaining in theory, you naturally look for ways to turn ideas into meaningful results.",

    whyThisCombinationWorks:
        "Your ability to analyse situations thoroughly is complemented by a practical focus on achieving results. Together, these qualities help you develop solutions that are both well thought out and effective in real-world situations.",

    blendStrengths: [
        "Practical reasoning",
        "Analytical thinking",
        "Execution",
        "Problem-solving"
    ],

    learningStyle:
        "You learn best when theory is supported by practical examples, demonstrations and real-world application.",

    idealEnvironment:
        "You thrive in environments where careful thinking leads to practical outcomes and measurable results."
  },

  {
    profile: "independent_problem_solver",

    introduction:
        "You are primarily an Analytical Thinker, but you also share strong characteristics with the Independent Problem Solver profile.",

    blendSummary:
        "Your analytical ability is strengthened by a strong sense of independence. You enjoy investigating difficult problems on your own, thinking deeply about possible solutions and taking ownership of complex challenges from start to finish.",

    whyThisCombinationWorks:
        "Your analytical thinking gives you the ability to understand complex challenges, while your independent nature gives you the confidence to tackle them on your own. Together, these qualities make you persistent, resourceful and capable of solving difficult problems.",

    blendStrengths: [
        "Independent thinking",
        "Analysis",
        "Persistence",
        "Complex problem-solving"
    ],

    learningStyle:
        "You learn best when given the freedom to investigate ideas independently and explore solutions at your own pace.",

    idealEnvironment:
        "You thrive in environments that value autonomy, intellectual challenge and deep analytical work."
  }


  ],

    creative_explorer: [

    {
        profile: "analytical_thinker",

        introduction:
        "You are primarily a Creative Explorer, but you also share strong characteristics with the Analytical Thinker profile.",

        blendSummary:
        "Your curiosity and imagination are strengthened by a logical approach to evaluating ideas. You enjoy exploring new possibilities while carefully analysing information, allowing you to balance creativity with thoughtful decision-making.",

        whyThisCombinationWorks:
        "Your creativity encourages you to generate fresh ideas, while your analytical thinking helps you evaluate and refine them. Together, these qualities allow you to develop solutions that are both imaginative and practical.",

        blendStrengths: [
        "Creative thinking",
        "Critical analysis",
        "Innovation",
        "Balanced problem-solving"
        ],

        learningStyle:
        "You learn best when you can explore new ideas freely before analysing them through structured reflection and practical application.",

        idealEnvironment:
        "You thrive in environments that encourage creativity while providing opportunities for thoughtful analysis and intellectual challenge."
    },

    {
        profile: "strategic_planner",

        introduction:
        "You are primarily a Creative Explorer, but you also share strong characteristics with the Strategic Planner profile.",

        blendSummary:
        "You naturally generate imaginative ideas while also enjoying organising them into clear plans. Rather than simply thinking creatively, you like turning possibilities into realistic goals that can be achieved over time.",

        whyThisCombinationWorks:
        "Your creativity opens the door to new possibilities, while your strategic thinking provides the structure needed to transform those ideas into achievable outcomes.",

        blendStrengths: [
        "Vision",
        "Strategic thinking",
        "Innovation",
        "Planning"
        ],

        learningStyle:
        "You learn best when creative exploration is combined with clear objectives, allowing you to experiment before organising your ideas into practical plans.",

        idealEnvironment:
        "You thrive in environments that encourage innovation while providing enough structure to turn ideas into meaningful results."
    },

    {
        profile: "versatile_collaborator",

        introduction:
        "You are primarily a Creative Explorer, but you also share strong characteristics with the Versatile Collaborator profile.",

        blendSummary:
        "You enjoy exploring new ideas and sharing them with others. Your creativity is enhanced by your ability to communicate openly, collaborate effectively and learn from different perspectives.",

        whyThisCombinationWorks:
        "Your imaginative thinking is complemented by your collaborative nature, allowing you to build ideas together with others and create solutions that benefit from diverse viewpoints.",

        blendStrengths: [
        "Creativity",
        "Communication",
        "Collaboration",
        "Adaptability"
        ],

        learningStyle:
        "You learn best through discussion, teamwork and creative projects that encourage the exchange of ideas and different perspectives.",

        idealEnvironment:
        "You thrive in environments that value creativity, collaboration and open communication."
    },

    {
        profile: "practical_operator",

        introduction:
        "You are primarily a Creative Explorer, but you also share strong characteristics with the Practical Operator profile.",

        blendSummary:
        "Your imagination is balanced by a practical mindset that enjoys turning ideas into action. Rather than leaving concepts unfinished, you naturally look for realistic ways to implement your creative solutions.",

        whyThisCombinationWorks:
        "Your creativity generates fresh ideas, while your practical nature helps you apply them effectively in real-world situations. Together, these qualities encourage innovation that leads to meaningful results.",

        blendStrengths: [
        "Innovation",
        "Practical execution",
        "Problem-solving",
        "Resourcefulness"
        ],

        learningStyle:
        "You learn best when creative concepts are supported by practical activities, demonstrations and opportunities to apply what you have learned.",

        idealEnvironment:
        "You thrive in environments where creativity is encouraged and practical application is equally valued."
    },

    {
        profile: "independent_problem_solver",

        introduction:
        "You are primarily a Creative Explorer, but you also share strong characteristics with the Independent Problem Solver profile.",

        blendSummary:
        "Your curiosity is strengthened by a strong sense of independence. You enjoy exploring complex ideas on your own, experimenting with different approaches and finding original solutions through self-directed thinking.",

        whyThisCombinationWorks:
        "Your creativity encourages exploration, while your independence gives you the confidence to investigate ideas without relying heavily on others. Together, these qualities make you resourceful, original and persistent when solving challenging problems.",

        blendStrengths: [
        "Independent thinking",
        "Creativity",
        "Originality",
        "Persistence"
        ],

        learningStyle:
        "You learn best when given the freedom to investigate ideas independently, experiment with different approaches and discover solutions through personal exploration.",

        idealEnvironment:
        "You thrive in environments that encourage independent thinking, creativity and the freedom to explore new ideas."
    }

    ],

    strategic_planner: [

    {
        profile: "analytical_thinker",

        introduction:
        "You are primarily a Strategic Planner, but you also share strong characteristics with the Analytical Thinker profile.",

        blendSummary:
        "You naturally think ahead and enjoy organising ideas into clear plans, while also carefully analysing information before making decisions. You prefer understanding the bigger picture before committing to a course of action.",

        whyThisCombinationWorks:
        "Your strategic mindset helps you focus on long-term goals, while your analytical thinking ensures your decisions are supported by careful reasoning. Together, these qualities help you make thoughtful, well-informed plans.",

        blendStrengths: [
        "Strategic thinking",
        "Analytical reasoning",
        "Planning",
        "Decision-making"
        ],

        learningStyle:
        "You learn best through structured learning that encourages careful analysis, long-term planning and logical problem-solving.",

        idealEnvironment:
        "You thrive in organised environments where thoughtful planning, critical thinking and well-reasoned decisions are valued."
    },

    {
        profile: "creative_explorer",

        introduction:
        "You are primarily a Strategic Planner, but you also share strong characteristics with the Creative Explorer profile.",

        blendSummary:
        "Your organised approach is complemented by a willingness to explore fresh ideas and different possibilities. You enjoy creating structured plans while remaining open to innovation when better solutions emerge.",

        whyThisCombinationWorks:
        "Your strategic thinking provides direction, while your creativity encourages flexibility and innovation. Together, these qualities allow you to plan carefully without becoming limited by routine thinking.",

        blendStrengths: [
        "Strategic vision",
        "Innovation",
        "Adaptability",
        "Creative planning"
        ],

        learningStyle:
        "You learn best when creative exploration is balanced with clear objectives and structured opportunities to develop your ideas.",

        idealEnvironment:
        "You thrive in environments that encourage innovation while maintaining clear goals, organisation and purposeful direction."
    },

    {
        profile: "versatile_collaborator",

        introduction:
        "You are primarily a Strategic Planner, but you also share strong characteristics with the Versatile Collaborator profile.",

        blendSummary:
        "You enjoy setting clear goals while working effectively with others to achieve them. Your organised thinking is strengthened by your ability to communicate openly, encourage teamwork and consider different perspectives.",

        whyThisCombinationWorks:
        "Your planning skills provide structure, while your collaborative nature helps build strong relationships and encourages shared success. Together, these qualities make you an effective organiser and team contributor.",

        blendStrengths: [
        "Leadership",
        "Organisation",
        "Communication",
        "Collaboration"
        ],

        learningStyle:
        "You learn best through structured projects, group discussions and opportunities to organise ideas while working with others.",

        idealEnvironment:
        "You thrive in environments that value planning, teamwork, shared responsibility and open communication."
    },

    {
        profile: "practical_operator",

        introduction:
        "You are primarily a Strategic Planner, but you also share strong characteristics with the Practical Operator profile.",

        blendSummary:
        "You naturally develop clear plans while maintaining a strong focus on achieving practical results. You enjoy organising tasks efficiently and ensuring that ideas lead to meaningful action.",

        whyThisCombinationWorks:
        "Your strategic planning provides direction, while your practical mindset focuses on execution. Together, these qualities help transform well-developed plans into successful outcomes.",

        blendStrengths: [
        "Planning",
        "Execution",
        "Organisation",
        "Reliability"
        ],

        learningStyle:
        "You learn best when clear plans are supported by practical exercises, real-world examples and opportunities to apply what you have learned.",

        idealEnvironment:
        "You thrive in environments where careful planning leads to measurable results and continuous improvement."
    },

    {
        profile: "independent_problem_solver",

        introduction:
        "You are primarily a Strategic Planner, but you also share strong characteristics with the Independent Problem Solver profile.",

        blendSummary:
        "You enjoy thinking ahead while maintaining the confidence to solve complex challenges independently. You prefer developing well-considered strategies before working methodically towards your goals with minimal supervision.",

        whyThisCombinationWorks:
        "Your strategic thinking provides long-term direction, while your independence allows you to take ownership of difficult challenges. Together, these qualities make you disciplined, resourceful and highly self-motivated.",

        blendStrengths: [
        "Independent thinking",
        "Strategic planning",
        "Self-management",
        "Problem-solving"
        ],

        learningStyle:
        "You learn best when given clear objectives alongside the freedom to investigate, plan and solve problems independently.",

        idealEnvironment:
        "You thrive in environments that value autonomy, strategic thinking, intellectual challenge and personal responsibility."
    }

    ],

    practical_operator: [

    {
        profile: "analytical_thinker",

        introduction:
        "You are primarily a Practical Operator, but you also share strong characteristics with the Analytical Thinker profile.",

        blendSummary:
        "You naturally focus on practical results while taking time to analyse situations carefully before acting. You enjoy finding solutions that are realistic, efficient and supported by sound reasoning.",

        whyThisCombinationWorks:
        "Your practical mindset keeps you focused on achieving results, while your analytical thinking helps you evaluate problems thoroughly before deciding on the best course of action.",

        blendStrengths: [
        "Practical problem-solving",
        "Logical thinking",
        "Reliability",
        "Sound judgement"
        ],

        learningStyle:
        "You learn best when theory is supported by practical examples that allow you to understand both why something works and how to apply it effectively.",

        idealEnvironment:
        "You thrive in environments where logical thinking, practical application and dependable execution are equally valued."
    },

    {
        profile: "creative_explorer",

        introduction:
        "You are primarily a Practical Operator, but you also share strong characteristics with the Creative Explorer profile.",

        blendSummary:
        "You enjoy turning creative ideas into practical solutions. Rather than pursuing innovation for its own sake, you naturally look for ways to make new ideas useful, achievable and valuable.",

        whyThisCombinationWorks:
        "Your creativity generates fresh possibilities, while your practical mindset ensures those ideas can be applied successfully in real-world situations.",

        blendStrengths: [
        "Innovation",
        "Practical application",
        "Adaptability",
        "Resourcefulness"
        ],

        learningStyle:
        "You learn best through practical projects that encourage experimentation while demonstrating clear real-world value.",

        idealEnvironment:
        "You thrive in environments where creativity is encouraged alongside practical thinking and measurable outcomes."
    },

    {
        profile: "strategic_planner",

        introduction:
        "You are primarily a Practical Operator, but you also share strong characteristics with the Strategic Planner profile.",

        blendSummary:
        "You enjoy working towards clear goals using organised plans that produce reliable results. You naturally combine careful preparation with a strong commitment to completing tasks effectively.",

        whyThisCombinationWorks:
        "Your practical nature focuses on getting things done, while your strategic thinking provides direction and organisation. Together, these qualities help you deliver consistent, well-planned results.",

        blendStrengths: [
        "Planning",
        "Execution",
        "Organisation",
        "Dependability"
        ],

        learningStyle:
        "You learn best when structured learning is combined with practical exercises that demonstrate how planning leads to successful outcomes.",

        idealEnvironment:
        "You thrive in organised environments where preparation, efficiency and consistent performance are appreciated."
    },

    {
        profile: "versatile_collaborator",

        introduction:
        "You are primarily a Practical Operator, but you also share strong characteristics with the Versatile Collaborator profile.",

        blendSummary:
        "You enjoy working with others to achieve practical goals. Your dependable approach is strengthened by your willingness to communicate openly, support your team and contribute to shared success.",

        whyThisCombinationWorks:
        "Your practical focus keeps projects moving forward, while your collaborative nature helps build strong working relationships. Together, these qualities make you a dependable and supportive contributor.",

        blendStrengths: [
        "Teamwork",
        "Reliability",
        "Communication",
        "Practical leadership"
        ],

        learningStyle:
        "You learn best through collaborative activities that combine discussion with practical tasks and shared responsibility.",

        idealEnvironment:
        "You thrive in environments where teamwork, practical achievement and mutual support are encouraged."
    },

    {
        profile: "independent_problem_solver",

        introduction:
        "You are primarily a Practical Operator, but you also share strong characteristics with the Independent Problem Solver profile.",

        blendSummary:
        "You prefer solving practical challenges independently while maintaining a strong focus on achieving effective results. You enjoy taking responsibility for tasks and finding efficient solutions through your own initiative.",

        whyThisCombinationWorks:
        "Your practical mindset keeps you focused on achievable outcomes, while your independence gives you the confidence to solve problems without constant guidance. Together, these qualities make you self-reliant, dependable and effective.",

        blendStrengths: [
        "Independence",
        "Practical reasoning",
        "Responsibility",
        "Problem-solving"
        ],

        learningStyle:
        "You learn best when given the freedom to work independently on practical challenges that require initiative and sound judgement.",

        idealEnvironment:
        "You thrive in environments that value responsibility, independence and practical achievement."
    }

    ],

        versatile_collaborator: [

    {
        profile: "analytical_thinker",

        introduction:
        "You are primarily a Versatile Collaborator, but you also share strong characteristics with the Analytical Thinker profile.",

        blendSummary:
        "You naturally enjoy working with people while also taking time to think carefully before making decisions. You balance strong interpersonal skills with logical reasoning, allowing you to contribute thoughtfully in both discussions and problem-solving.",

        whyThisCombinationWorks:
        "Your collaborative nature helps you build strong relationships, while your analytical thinking ensures your ideas are carefully considered. Together, these qualities help you communicate with both confidence and clarity.",

        blendStrengths: [
        "Communication",
        "Critical thinking",
        "Teamwork",
        "Balanced decision-making"
        ],

        learningStyle:
        "You learn best through discussion, collaboration and opportunities to analyse ideas before applying them in practical situations.",

        idealEnvironment:
        "You thrive in environments that encourage teamwork, open communication and thoughtful problem-solving."
    },

    {
        profile: "creative_explorer",

        introduction:
        "You are primarily a Versatile Collaborator, but you also share strong characteristics with the Creative Explorer profile.",

        blendSummary:
        "You enjoy working closely with others while bringing fresh ideas and creative energy into every project. You naturally encourage innovation by listening to different perspectives and building on them together.",

        whyThisCombinationWorks:
        "Your collaborative mindset creates opportunities for shared success, while your creativity encourages original thinking. Together, these qualities help teams develop innovative and practical solutions.",

        blendStrengths: [
        "Creativity",
        "Communication",
        "Adaptability",
        "Collaboration"
        ],

        learningStyle:
        "You learn best through interactive activities, group discussions and creative projects that encourage teamwork and exploration.",

        idealEnvironment:
        "You thrive in environments where creativity, teamwork and open communication are equally encouraged."
    },

    {
        profile: "strategic_planner",

        introduction:
        "You are primarily a Versatile Collaborator, but you also share strong characteristics with the Strategic Planner profile.",

        blendSummary:
        "You enjoy bringing people together around clear goals and organised plans. Your natural ability to communicate effectively is strengthened by a thoughtful, structured approach to achieving success.",

        whyThisCombinationWorks:
        "Your collaborative approach helps people work well together, while your strategic thinking provides direction and purpose. Together, these qualities allow you to guide teams towards shared objectives.",

        blendStrengths: [
        "Leadership",
        "Organisation",
        "Communication",
        "Team coordination"
        ],

        learningStyle:
        "You learn best through structured group activities that combine planning, discussion and shared responsibility.",

        idealEnvironment:
        "You thrive in environments that encourage teamwork, leadership and purposeful collaboration."
    },

    {
        profile: "practical_operator",

        introduction:
        "You are primarily a Versatile Collaborator, but you also share strong characteristics with the Practical Operator profile.",

        blendSummary:
        "You enjoy supporting others while maintaining a practical focus on achieving results. You naturally help teams stay organised, productive and committed to completing meaningful work together.",

        whyThisCombinationWorks:
        "Your collaborative nature strengthens relationships, while your practical mindset ensures ideas are translated into effective action. Together, these qualities make you a dependable and encouraging team member.",

        blendStrengths: [
        "Teamwork",
        "Practical leadership",
        "Reliability",
        "Execution"
        ],

        learningStyle:
        "You learn best through collaborative projects that involve practical tasks, shared responsibilities and real-world application.",

        idealEnvironment:
        "You thrive in environments where cooperation, practical achievement and mutual support are highly valued."
    },

    {
        profile: "independent_problem_solver",

        introduction:
        "You are primarily a Versatile Collaborator, but you also share strong characteristics with the Independent Problem Solver profile.",

        blendSummary:
        "You work well with others while also feeling confident taking initiative when challenges arise. You know when to collaborate and when to work independently, allowing you to adapt to different situations with ease.",

        whyThisCombinationWorks:
        "Your collaborative strengths help you build productive relationships, while your independence gives you the confidence to solve problems proactively. Together, these qualities make you both supportive and self-reliant.",

        blendStrengths: [
        "Adaptability",
        "Initiative",
        "Communication",
        "Problem-solving"
        ],

        learningStyle:
        "You learn best when given opportunities to collaborate with others while also having the freedom to investigate and solve problems independently.",

        idealEnvironment:
        "You thrive in environments that value teamwork, initiative, flexibility and continuous learning."
    }

    ],

        independent_problem_solver: [

    {
        profile: "analytical_thinker",

        introduction:
        "You are primarily an Independent Problem Solver, but you also share strong characteristics with the Analytical Thinker profile.",

        blendSummary:
        "You enjoy solving complex problems independently while carefully analysing information before making decisions. Your confidence in working alone is strengthened by a logical, evidence-based approach to understanding difficult challenges.",

        whyThisCombinationWorks:
        "Your independence allows you to tackle challenges with confidence, while your analytical thinking ensures your solutions are well reasoned and carefully evaluated. Together, these qualities make you a thoughtful and capable problem solver.",

        blendStrengths: [
        "Independent thinking",
        "Analytical reasoning",
        "Critical thinking",
        "Problem-solving"
        ],

        learningStyle:
        "You learn best when given the freedom to investigate problems independently while applying careful analysis to reach well-supported conclusions.",

        idealEnvironment:
        "You thrive in environments that value autonomy, intellectual challenge and evidence-based decision-making."
    },

    {
        profile: "creative_explorer",

        introduction:
        "You are primarily an Independent Problem Solver, but you also share strong characteristics with the Creative Explorer profile.",

        blendSummary:
        "You enjoy exploring new ideas independently and are naturally curious about different ways of solving problems. Your originality is strengthened by your willingness to experiment and discover solutions through your own initiative.",

        whyThisCombinationWorks:
        "Your independence gives you the confidence to explore ideas on your own, while your creativity encourages innovative thinking. Together, these qualities help you develop original and effective solutions.",

        blendStrengths: [
        "Creativity",
        "Original thinking",
        "Independence",
        "Innovation"
        ],

        learningStyle:
        "You learn best when given the freedom to explore ideas independently, experiment with different approaches and learn through discovery.",

        idealEnvironment:
        "You thrive in environments that encourage creativity, independent exploration and continuous learning."
    },

    {
        profile: "strategic_planner",

        introduction:
        "You are primarily an Independent Problem Solver, but you also share strong characteristics with the Strategic Planner profile.",

        blendSummary:
        "You prefer working independently while thinking carefully about long-term goals and the best way to achieve them. You naturally combine self-direction with thoughtful planning and disciplined decision-making.",

        whyThisCombinationWorks:
        "Your independence allows you to take ownership of complex challenges, while your strategic thinking helps you approach them with structure and purpose. Together, these qualities encourage disciplined and effective problem-solving.",

        blendStrengths: [
        "Strategic thinking",
        "Self-management",
        "Planning",
        "Independent decision-making"
        ],

        learningStyle:
        "You learn best when given clear objectives together with the freedom to develop your own plans and solve problems independently.",

        idealEnvironment:
        "You thrive in environments that value autonomy, strategic thinking and personal responsibility."
    },

    {
        profile: "practical_operator",

        introduction:
        "You are primarily an Independent Problem Solver, but you also share strong characteristics with the Practical Operator profile.",

        blendSummary:
        "You enjoy solving practical challenges on your own and finding realistic solutions that can be applied effectively. Your independent nature is balanced by a strong focus on achieving reliable, measurable results.",

        whyThisCombinationWorks:
        "Your independence gives you confidence to take initiative, while your practical mindset ensures your ideas lead to useful outcomes. Together, these qualities make you dependable, resourceful and action-oriented.",

        blendStrengths: [
        "Practical problem-solving",
        "Initiative",
        "Reliability",
        "Execution"
        ],

        learningStyle:
        "You learn best through practical challenges that allow you to investigate, apply your knowledge and work independently towards clear outcomes.",

        idealEnvironment:
        "You thrive in environments that encourage initiative, practical achievement and independent responsibility."
    },

    {
        profile: "versatile_collaborator",

        introduction:
        "You are primarily an Independent Problem Solver, but you also share strong characteristics with the Versatile Collaborator profile.",

        blendSummary:
        "Although you value working independently, you also recognise the importance of collaborating when different perspectives can strengthen a solution. You are comfortable taking ownership of your work while contributing positively to shared goals.",

        whyThisCombinationWorks:
        "Your independence helps you approach challenges with confidence, while your collaborative strengths allow you to build productive relationships when teamwork is beneficial. Together, these qualities make you adaptable and dependable in a wide range of situations.",

        blendStrengths: [
        "Independence",
        "Collaboration",
        "Adaptability",
        "Communication"
        ],

        learningStyle:
        "You learn best when you have the flexibility to investigate problems independently while also having opportunities to discuss ideas and learn from others.",

        idealEnvironment:
        "You thrive in environments that balance autonomy with collaboration, allowing you to contribute both independently and as part of a team."
    }

    ]
};